import type {
  IIsQueryKeyForProjectProps,
  IKeepPreviousDataForProjectProps,
  IPreviousQueryWithKey,
} from "./project-query-key.types";

// NOTE: NSAI-4910 — project-scoped queries carry the project uuid in their
// React Query key, because the project reaches the server through the ambient
// `X-Project-Uuid` header rather than through an argument the key would
// otherwise capture. This tells whether a cached entry belongs to a given
// project, so a project switch can treat the entries of the project it left
// differently from everything else: invalidating them with a refetch would
// send the request under the NEW project's header and store that response in
// the OLD project's cache slot, which then renders on the way back.
// It matches TOP-LEVEL key segments only: a uuid nested inside an object
// segment (`[KEY, { projectUuid }]`) is not seen, and such a key would fall
// back into the refetching invalidation. Keys put the uuid at the top level.
export const isQueryKeyForProject = ({
  queryKey,
  projectUuid,
}: IIsQueryKeyForProjectProps): boolean =>
  Boolean(projectUuid) && queryKey.includes(projectUuid);

// NOTE: NSAI-4910 — a project-aware drop-in for TanStack's `keepPreviousData`.
// That one returns the previous rows unconditionally, so when the key flips to
// a new project (a switch while the screen stays mounted) it paints the old
// project's rows under the new one until the fetch lands. This keeps them only
// while the previous query belongs to the current project. The returned
// callback takes two arguments because that is `placeholderData`'s contract.
export const keepPreviousDataForProject =
  ({ projectUuid }: IKeepPreviousDataForProjectProps) =>
  <TData>(
    previousData: TData | undefined,
    previousQuery: IPreviousQueryWithKey | undefined,
  ): TData | undefined =>
    previousQuery &&
    isQueryKeyForProject({ queryKey: previousQuery.queryKey, projectUuid })
      ? previousData
      : undefined;
